// Pure functions for "Por dia" aggregation — no React/DOM dependencies.
// Used by client pages and tested via Vitest.
// Note: uses only Array.from / map.forEach for Map iteration (tsconfig target compatibility).

export interface CorInfo {
  id: string;
  nome: string;
  hexColor: string;
  ordem: number;
}

export interface SetorInfo {
  id: string;
  nome: string;
  variedade: { id: string; nome: string };
}

export interface ColheitaInput {
  id: string;
  setorId: string;
  corFitaId?: string | null;
  dataColheita: string; // YYYY-MM-DD
  quantidadeCachos: number;
  setor: SetorInfo;
  corFita?: CorInfo | null;
}

export interface MangaraInput {
  id: string;
  setorId: string;
  corFitaId: string;
  dataCorteMangara: string; // YYYY-MM-DD
  quantidadeCachos: number;
  setor: SetorInfo;
  corFita: CorInfo;
}

// ── ISO week helper ────────────────────────────────────────────────────────────

function isoWeek(dateStr: string): number {
  const date = new Date(dateStr + 'T00:00:00Z');
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// ── Colheitas por dia ──────────────────────────────────────────────────────────

export interface FitaColheita {
  corId: string;
  corNome: string;
  hexColor: string;
  quantidade: number;
}

export interface SetorColheita {
  setorId: string;
  nome: string;
  variedadeId: string;
  variedadeNome: string;
  totalCachos: number;
  fitas: FitaColheita[];
  isMinoria: boolean; // variedade diferente da predominante do dia
}

export interface DiaColheita {
  data: string; // YYYY-MM-DD
  semana: number;
  totalCachos: number;
  setores: SetorColheita[];
}

export function agruparColheitasPorDia(
  colheitas: ColheitaInput[],
  coresOrdenadas: CorInfo[]
): DiaColheita[] {
  // Group by date using plain object
  const byDate: Record<string, ColheitaInput[]> = {};
  colheitas.forEach(c => {
    if (!byDate[c.dataColheita]) byDate[c.dataColheita] = [];
    byDate[c.dataColheita].push(c);
  });

  // Index cores by id → position for sorting
  const coresIdx: Record<string, number> = {};
  coresOrdenadas.forEach((cor, i) => { coresIdx[cor.id] = i; });

  const dias: DiaColheita[] = Object.keys(byDate).map(data => {
    const items = byDate[data];

    // Count cachos by variedade to find predominant
    const cachosPorVar: Record<string, number> = {};
    items.forEach(c => {
      const vId = c.setor.variedade.id;
      cachosPorVar[vId] = (cachosPorVar[vId] ?? 0) + c.quantidadeCachos;
    });
    let predominanteId = '';
    let maxCachos = -1;
    Object.keys(cachosPorVar).forEach(vId => {
      if (cachosPorVar[vId] > maxCachos) { maxCachos = cachosPorVar[vId]; predominanteId = vId; }
    });

    // Group by setor
    const bySetor: Record<string, ColheitaInput[]> = {};
    items.forEach(c => {
      if (!bySetor[c.setorId]) bySetor[c.setorId] = [];
      bySetor[c.setorId].push(c);
    });

    // Build setor rows
    const setorRows: SetorColheita[] = Object.keys(bySetor).map(setorId => {
      const sitems = bySetor[setorId];
      const s = sitems[0].setor;

      // Aggregate by cor
      const corMap: Record<string, number> = {};
      sitems.forEach(c => {
        if (c.corFitaId && c.corFita) {
          corMap[c.corFitaId] = (corMap[c.corFitaId] ?? 0) + c.quantidadeCachos;
        }
      });

      // Sort fitas by cor.ordem
      const sortedCorIds = Object.keys(corMap).sort((a, b) => {
        return (coresIdx[a] ?? 9999) - (coresIdx[b] ?? 9999);
      });

      const fitas: FitaColheita[] = sortedCorIds.reduce<FitaColheita[]>((acc, corId) => {
        const cor = coresOrdenadas.find(c => c.id === corId);
        if (cor) acc.push({ corId, corNome: cor.nome, hexColor: cor.hexColor, quantidade: corMap[corId] });
        return acc;
      }, []);

      const totalCachos = fitas.reduce((sum, f) => sum + f.quantidade, 0);
      return {
        setorId,
        nome: s.nome,
        variedadeId: s.variedade.id,
        variedadeNome: s.variedade.nome,
        totalCachos,
        fitas,
        isMinoria: s.variedade.id !== predominanteId,
      };
    });

    setorRows.sort((a, b) => a.nome.localeCompare(b.nome));

    return {
      data,
      semana: isoWeek(data),
      totalCachos: setorRows.reduce((sum, r) => sum + r.totalCachos, 0),
      setores: setorRows,
    };
  });

  // Sort dates descending
  dias.sort((a, b) => b.data.localeCompare(a.data));
  return dias;
}

// ── Mangarás por dia ───────────────────────────────────────────────────────────

export interface SetorMangara {
  setorId: string;
  nome: string;
  variedadeId: string;
  variedadeNome: string;
  quantidade: number;
  isMinoria: boolean;
}

export interface FitaMangara {
  corId: string;
  corNome: string;
  hexColor: string;
  total: number;
  setores: SetorMangara[];
}

export interface DiaMangara {
  data: string; // YYYY-MM-DD
  semana: number;
  totalMangaras: number;
  totalSetores: number;
  fitas: FitaMangara[];
}

export function agruparMangarasPorDia(
  mangaras: MangaraInput[],
  coresOrdenadas: CorInfo[]
): DiaMangara[] {
  const byDate: Record<string, MangaraInput[]> = {};
  mangaras.forEach(m => {
    if (!byDate[m.dataCorteMangara]) byDate[m.dataCorteMangara] = [];
    byDate[m.dataCorteMangara].push(m);
  });

  const coresIdx: Record<string, number> = {};
  coresOrdenadas.forEach((cor, i) => { coresIdx[cor.id] = i; });

  const dias: DiaMangara[] = Object.keys(byDate).map(data => {
    const items = byDate[data];

    // Predominant variety
    const cachosPorVar: Record<string, number> = {};
    items.forEach(m => {
      const vId = m.setor.variedade.id;
      cachosPorVar[vId] = (cachosPorVar[vId] ?? 0) + m.quantidadeCachos;
    });
    let predominanteId = '';
    let maxCachos = -1;
    Object.keys(cachosPorVar).forEach(vId => {
      if (cachosPorVar[vId] > maxCachos) { maxCachos = cachosPorVar[vId]; predominanteId = vId; }
    });

    // Group by cor
    const byCor: Record<string, MangaraInput[]> = {};
    items.forEach(m => {
      if (!byCor[m.corFitaId]) byCor[m.corFitaId] = [];
      byCor[m.corFitaId].push(m);
    });

    // Sort cors by ordem
    const sortedCorIds = Object.keys(byCor).sort((a, b) => {
      return (coresIdx[a] ?? 9999) - (coresIdx[b] ?? 9999);
    });

    const allSetorIds = new Set<string>();

    const fitas: FitaMangara[] = sortedCorIds.map(corId => {
      const mitems = byCor[corId];
      const cor = coresOrdenadas.find(c => c.id === corId) ?? mitems[0].corFita;

      // Group by setor
      const bySetor: Record<string, number> = {};
      mitems.forEach(m => {
        bySetor[m.setorId] = (bySetor[m.setorId] ?? 0) + m.quantidadeCachos;
        allSetorIds.add(m.setorId);
      });

      const setores: SetorMangara[] = Object.keys(bySetor).map(setorId => {
        const s = mitems.find(m => m.setorId === setorId)!.setor;
        return {
          setorId,
          nome: s.nome,
          variedadeId: s.variedade.id,
          variedadeNome: s.variedade.nome,
          quantidade: bySetor[setorId],
          isMinoria: s.variedade.id !== predominanteId,
        };
      });
      setores.sort((a, b) => a.nome.localeCompare(b.nome));

      const total = setores.reduce((sum, r) => sum + r.quantidade, 0);
      return { corId, corNome: cor.nome, hexColor: cor.hexColor, total, setores };
    });

    return {
      data,
      semana: isoWeek(data),
      totalMangaras: fitas.reduce((sum, f) => sum + f.total, 0),
      totalSetores: allSetorIds.size,
      fitas,
    };
  });

  dias.sort((a, b) => b.data.localeCompare(a.data));
  return dias;
}

// ── Texto WhatsApp ─────────────────────────────────────────────────────────────

function formatDataBR(dateStr: string): string {
  // YYYY-MM-DD → DD-MM-AAAA
  const parts = dateStr.split('-');
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

function fitaPlural(n: number): string {
  return n === 1 ? 'fita' : 'fitas';
}

function mangaraPlural(n: number): string {
  return n === 1 ? 'mangará' : 'mangarás';
}

function setorNome(nome: string, variedadeNome: string, isMinoria: boolean): string {
  if (!isMinoria) return nome;
  return `${nome} banana ${variedadeNome.toLowerCase()}`;
}

export function textoWhatsAppColheita(dia: DiaColheita): string {
  const linhas: string[] = [
    'Boa noite',
    `Cachos colhidos dia ${formatDataBR(dia.data)}`,
  ];
  dia.setores.forEach(setor => {
    linhas.push(setorNome(setor.nome, setor.variedadeNome, setor.isMinoria));
    setor.fitas.forEach(fita => {
      linhas.push(`${fita.quantidade} ${fitaPlural(fita.quantidade)} ${fita.corNome.toLowerCase()}`);
    });
  });
  return linhas.join('\n');
}

export function textoWhatsAppMangara(dia: DiaMangara): string {
  const linhas: string[] = [
    'Boa noite',
    'Corte de mangaras',
    `Dia ${formatDataBR(dia.data)}`,
  ];
  dia.fitas.forEach(fita => {
    const corCap = fita.corNome.charAt(0).toUpperCase() + fita.corNome.slice(1).toLowerCase();
    linhas.push(`Fitas ${corCap}`);
    fita.setores.forEach(setor => {
      linhas.push(setorNome(setor.nome, setor.variedadeNome, setor.isMinoria));
      linhas.push(`${setor.quantidade} ${mangaraPlural(setor.quantidade)}`);
    });
  });
  return linhas.join('\n');
}
